import { definedMap, setMap, withDefault } from "collection-utils";
import first from "lodash/first";
import last from "lodash/last";

import { BasePrimitiveValue } from "../../../components/computation-types";
import { SerializedComponent, getAppTabs } from "../../../components/SerializedApp";
import { logError, logInfo } from "../../../debug-print";
import {
    asMaybeTrueOrFalse,
    assert,
    assertNever,
    checkString,
    defined,
    mapFilterUndefined,
    panic,
} from "../../../Support";
import { getAllowedAndSelectedColumns, getAllowedColumnsForProperty } from "../../allowed-columns";
import { makeValueForSourceColumn } from "../../builder-utils";
import {
    Description,
    InputOutputTables,
    MutatingScreenKind,
    PropertyDescription,
    PropertyKind,
    SourceColumn,
    SourceColumnKind,
    SpecialValueKind,
    TableColumn,
    TableGlideType,
    getSourceColumnPath,
    getTableColumnDisplayName,
    getTableName,
    getTableRefTableName,
    isSingleRelationType,
    makeSourceColumn,
    sheetNameForTable,
    ColumnType,
    areTableNamesEqual,
    TableAndColumn,
    ActionDescription,
} from "../../description";
import { findFittingColumn } from "../../description-utils";
import { ElementBuilder } from "../../serialized-component";
import { resolveSourceColumn } from "../../source-column";
import {
    getColumnForProperty,
    getEditedColumnsForProperties,
    makeDefaultAction,
    rewriteFilter,
} from "../descriptor-utils";
import { populateDescription } from "../populate-description";
import {
    getActionProperty,
    getArrayProperty,
    getColumnProperty,
    getCompoundActionProperty,
    getEnumProperty,
    getFilterProperty,
    getIconProperty,
    getNumberProperty,
    getScreenProperty,
    getSourceColumnProperty,
    getStringProperty,
    getSwitchProperty,
    getWebhookProperty,
    getZapProperty,
    makeEnumProperty,
    makeFilterProperty,
    makeScreenProperty,
} from "../property-utils";
import { findSpecialValueDescriptor } from "../special-values";
import { CompatibilityHelper, DescriptionHandler, PopulationMode, registerDescriptionHandler, SearchedColumn } from ".";
import {
    ArrayPropertyDescriptorCase,
    ColumnPropertyDescriptorCase,
    ComponentBuilder,
    ComponentConfiguratorContext,
    CompoundActionPropertyDescriptorCase,
    ConstantPropertyDescriptorCase,
    EnumPropertyDescriptorCase,
    FilterPropertyDescriptorCase,
    FormulaPropertyDescriptorCase,
    NumberPropertyDescriptorCase,
    PaymentMethodPropertyDescriptorCase,
    PropertyDescriptor,
    PropertyDescriptorCase,
    RequiredKind,
    RewritingComponentConfiguratorContext,
    SchemaInspector,
    ScreenPropertyDescriptorCase,
    SortsPropertyDescriptorCase,
    SpecialValuePropertyDescriptorCase,
    StringPropertyDescriptorCase,
    SwitchPropertyDescriptorCase,
    TablePropertyDescriptorCase,
    TransformsPropertyDescriptorCase,
    ZapPropertyDescriptorCase,
    getFilterTable,
    updatesForProperty,
    updatesForPropertyValue,
    updatesForUntypedProperty,
    IconPropertyDescriptorCase,
    WebhookPropertyDescriptorCase,
    ActionPropertyDescriptorCase,
    EditedColumns,
    EditedColumn,
} from "..";
import { handlerForActionKind } from "../../actions";
import { FlowActionNode, processCompoundActionFlow } from "../../actions/compound";

abstract class BaseDescriptionHandler<TDesc extends Description, TDescriptorCase extends PropertyDescriptorCase<TDesc>>
    implements DescriptionHandler<TDesc, TDescriptorCase>
{
    public readonly influencesOtherDescriptors: boolean = false;

    constructor(public readonly kind: PropertyKind) {}

    public abstract defaultUpdateForPropertyCase(
        descr: PropertyDescriptor,
        rootDesc: TDesc,
        desc: TDesc,
        pdc: TDescriptorCase,
        avoidDirectColumns: Set<TableColumn>,
        avoidEditedColumns: Set<TableColumn>,
        avoidIndirectColumns: Set<TableColumn>,
        tables: InputOutputTables,
        mutatingScreenKind: MutatingScreenKind | undefined,
        ccc: ComponentConfiguratorContext,
        populationMode: PopulationMode,
        getDefaultCaption: () => string | undefined
    ): Partial<TDesc> | undefined;

    public getEditedColumns(
        _pdc: TDescriptorCase,
        _pd: PropertyDescription,
        _rootDesc: TDesc,
        _tables: InputOutputTables,
        _mutatingScreenKind: MutatingScreenKind | undefined,
        _ccc: ComponentConfiguratorContext,
        _withActions: boolean
    ): EditedColumns {
        return [];
    }

    public rewriteWithCase(
        _descr: PropertyDescriptor,
        _pdc: TDescriptorCase,
        _pd: PropertyDescription,
        _newRootDesc: TDesc,
        _newDesc: TDesc,
        _tables: InputOutputTables,
        _mutatingScreenKind: MutatingScreenKind | undefined,
        _ignoreRequired: boolean,
        _ccc: RewritingComponentConfiguratorContext
    ): Partial<TDesc> | undefined {
        return {};
    }

    public makeValueForPropertyDescription<X, U>(
        _b: ElementBuilder<X>,
        pd: PropertyDescription,
        _undefinedIfEmpty: boolean,
        _targetType: ColumnType | undefined,
        _makeGetColumn: (sourceColumn: SourceColumn) => X | U
    ): X | U | undefined {
        return panic(`Cannot make value for property of kind ${pd.kind}`);
    }

    public abstract getColumnAssignmentName(_pd: PropertyDescription): string;

    public getColumnAssignmentIcon(_pd: PropertyDescription): string {
        return "componentSeparator";
    }

    public getColumnAssignmentLabel(_schema: SchemaInspector, pd: PropertyDescription, _table: TableGlideType): string {
        return panic(`Assignment for form not supported ${JSON.stringify(pd)}`);
    }

    public isSearchedColumn(
        _searched: SearchedColumn,
        _schema: SchemaInspector,
        _pd: PropertyDescription,
        _table: TableGlideType
    ): boolean {
        return false;
    }

    public makeArgumentForComponent(
        _builder: ComponentBuilder,
        _table: TableGlideType,
        _pd: PropertyDescription,
        p: TDescriptorCase
    ): SerializedComponent | undefined {
        return panic(`Property kind ${p.kind} not supported in simple component handler`);
    }

    public checkCompatibility(
        _propertyDescription: PropertyDescription,
        _desc: TDesc,
        _propertyDescriptor: PropertyDescriptor,
        _oldTables: InputOutputTables,
        _helper: CompatibilityHelper
    ): void {
        return;
    }
}

class ColumnDescriptionHandler<TDesc extends Description> extends BaseDescriptionHandler<
    TDesc,
    ColumnPropertyDescriptorCase<TDesc>
> {
    constructor() {
        super(PropertyKind.Column);
    }

    public defaultUpdateForPropertyCase(
        descr: PropertyDescriptor,
        rootDesc: TDesc,
        desc: TDesc,
        pdc: ColumnPropertyDescriptorCase<TDesc>,
        avoidDirectColumns: Set<TableColumn>,
        avoidEditedColumns: Set<TableColumn>,
        avoidIndirectColumns: Set<TableColumn>,
        tables: InputOutputTables,
        mutatingScreenKind: MutatingScreenKind | undefined,
        ccc: ComponentConfiguratorContext,
        populationMode: PopulationMode
    ): Partial<TDesc> | undefined {
        if (populationMode === PopulationMode.Rewrite && !pdc.required) {
            return updatesForProperty(descr.property, desc, undefined, undefined);
        }

        const acceptedColumns = getAllowedColumnsForProperty(pdc, tables, rootDesc, desc, ccc, mutatingScreenKind);
        const isIndirect = pdc.getIndirectTable !== undefined;
        const avoidColumns = isIndirect
            ? avoidIndirectColumns
            : pdc.isEditedInApp === true
            ? avoidEditedColumns
            : avoidDirectColumns;
        const avoidColumnNames = setMap(avoidColumns, c => c.name);
        const avoidColumnNamesSecondary =
            avoidColumns === avoidEditedColumns ? setMap(avoidDirectColumns, c => c.name) : undefined;
        const excludeColumnNamesFromProperties = mapFilterUndefined(
            withDefault(pdc.ignoreColumnsFromProperties, []),
            c => getColumnProperty((desc as any)[c])
        );

        // First, try avoiding the column names from other properties.
        // If we can't do that, and the property is required, try again
        // with allowing those, too.
        let column = findFittingColumn(
            acceptedColumns.allowed,
            acceptedColumns.avoid,
            withDefault(pdc.preferredNames, []),
            avoidColumnNames,
            avoidColumnNamesSecondary,
            new Set(excludeColumnNamesFromProperties),
            pdc.preferredType
        );
        if (column === undefined && pdc.required && excludeColumnNamesFromProperties.length > 0) {
            column = findFittingColumn(
                acceptedColumns.allowed,
                acceptedColumns.avoid,
                withDefault(pdc.preferredNames, []),
                avoidColumnNames,
                avoidColumnNamesSecondary,
                undefined,
                pdc.preferredType
            );
        }

        if (column === undefined || (populationMode === PopulationMode.Default && pdc.emptyByDefault === true)) {
            if (pdc.required && pdc.emptyByDefault !== true) return undefined;
            return updatesForProperty(descr.property, desc, undefined, undefined);
        } else {
            avoidColumns.add(column);
            if (pdc.isEditedInApp === true) {
                avoidEditedColumns.add(column);
            }
            return updatesForPropertyValue(pdc.kind, descr.property, desc, makeSourceColumn(column.name), undefined);
        }
    }

    private resolveColumn(
        pd: PropertyDescription,
        table: TableGlideType,
        schema: SchemaInspector
    ): TableAndColumn | undefined {
        const sc = getSourceColumnProperty(pd);
        if (sc === undefined) return undefined;
        const resolved = resolveSourceColumn(schema, sc, table, undefined);
        if (resolved === undefined) return undefined;
        return defined(last(resolved));
    }

    public getEditedColumns(
        pdc: ColumnPropertyDescriptorCase<TDesc>,
        pd: PropertyDescription,
        rootDesc: TDesc,
        tables: InputOutputTables,
        _mutatingScreenKind: MutatingScreenKind | undefined,
        ccc: ComponentConfiguratorContext
    ): EditedColumns {
        if (pdc.isEditedInApp !== true) return [];

        let outputTable: TableGlideType | undefined = tables.output;
        if (pdc.getIndirectTable !== undefined) {
            outputTable = pdc.getIndirectTable(tables, rootDesc, ccc);
            if (outputTable === undefined) return [];
        }

        const resolved = this.resolveColumn(pd, outputTable, ccc);
        if (resolved === undefined) return [];

        const { table: t, column: c } = resolved;
        return [[c.name, pdc.getIndirectTable === undefined, getTableName(t)]];
    }

    public rewriteWithCase(
        descr: PropertyDescriptor,
        pdc: ColumnPropertyDescriptorCase<TDesc>,
        pd: PropertyDescription,
        newRootDesc: TDesc,
        newDesc: TDesc,
        tables: InputOutputTables,
        mutatingScreenKind: MutatingScreenKind | undefined,
        ignoreRequired: boolean,
        ccc: RewritingComponentConfiguratorContext
    ): Partial<TDesc> | undefined {
        const sourceColumn = getSourceColumnProperty(pd);
        const allowedColumns = getAllowedAndSelectedColumns(
            pdc,
            tables,
            sourceColumn,
            newRootDesc,
            newDesc,
            ccc,
            mutatingScreenKind
        );
        if (allowedColumns?.selectedColumn === undefined) {
            if (!ignoreRequired && pdc.required) return undefined;

            return updatesForProperty(descr.property, newDesc, undefined, undefined);
        }
        return {};
    }

    public makeValueForPropertyDescription<X, U>(
        _b: ElementBuilder<X>,
        pd: PropertyDescription,
        _undefinedIfEmpty: boolean,
        _targetType: ColumnType | undefined,
        makeGetColumn: (sourceColumn: SourceColumn) => X | U
    ): X | U {
        return makeGetColumn(defined(getSourceColumnProperty(pd)));
    }

    public getColumnAssignmentName(pd: PropertyDescription): string {
        const sc = defined(getSourceColumnProperty(pd));
        switch (sc.kind) {
            case SourceColumnKind.DefaultContext:
                return "Screen column";
            case SourceColumnKind.UserProfile:
                return "User profile column";
            case SourceColumnKind.ContainingScreen:
                return "Screen column";
            default:
                return assertNever(sc.kind);
        }
    }

    public getColumnAssignmentIcon(): string {
        return "componentColumn";
    }

    public getColumnAssignmentLabel(
        schemaInspector: SchemaInspector,
        pd: PropertyDescription,
        table: TableGlideType
    ): string {
        const resolved = this.resolveColumn(pd, table, schemaInspector);
        if (resolved !== undefined) {
            return getTableColumnDisplayName(resolved.column);
        } else {
            const sc = defined(getSourceColumnProperty(pd));
            return defined(last(getSourceColumnPath(sc)));
        }
    }

    public isSearchedColumn(
        searched: SearchedColumn,
        schema: SchemaInspector,
        pd: PropertyDescription,
        table: TableGlideType
    ): boolean {
        if (searched.columnName === undefined || searched.tableName === undefined) return false;
        const resolved = this.resolveColumn(pd, table, schema);
        if (resolved === undefined) return false;
        return (
            searched.columnName === resolved.column.name &&
            areTableNamesEqual(searched.tableName, getTableName(resolved.table))
        );
    }

    public makeArgumentForComponent(
        builder: ComponentBuilder,
        table: TableGlideType,
        pd: PropertyDescription,
        p: ColumnPropertyDescriptorCase<TDesc>
    ): SerializedComponent | undefined {
        const sourceColumn = getSourceColumnProperty(pd);
        if (sourceColumn === undefined) return undefined;

        return makeValueForSourceColumn(
            builder,
            sourceColumn,
            builder.b.getContext,
            table,
            undefined,
            undefined,
            p.applyFormat === true
        )?.value;
    }

    public checkCompatibility(
        propertyDescription: PropertyDescription,
        desc: TDesc,
        propertyDescriptor: PropertyDescriptor,
        oldTables: InputOutputTables,
        helper: CompatibilityHelper
    ): void {
        // FIXME: use source column
        const columnName = getColumnProperty(propertyDescription);
        if (columnName === undefined) return undefined;

        const { screenName } = helper;

        const oldColumnResult = getColumnForProperty(
            propertyDescriptor,
            desc,
            oldTables,
            helper.oldComponentConfiguratorContext
        );
        if (oldColumnResult === undefined) {
            logError(`Old column ${columnName} not found, for screen ${screenName}`);
            return;
        }

        const newTable = helper.findTable(getTableName(oldColumnResult.table));
        if (newTable === undefined) return undefined;

        const newColumn = helper.findNewColumn(newTable, columnName);
        if (newColumn === undefined) return;

        const oldColumn = oldColumnResult.column;
        const newTableDisplayName = sheetNameForTable(newTable);
        if (isSingleRelationType(oldColumn.type)) {
            // A table reference must point to the same table.
            if (!isSingleRelationType(newColumn.type)) {
                helper.addOtherIssue(
                    `Column ${columnName} in sheet ${newTableDisplayName} is not a relation, but has type ${newColumn.type.kind}, for screen ${screenName}`
                );
                return;
            }
            if (getTableRefTableName(oldColumn.type).name !== getTableRefTableName(newColumn.type).name) {
                helper.addOtherIssue(
                    `Column ${columnName} in sheet ${newTableDisplayName} does not relate to table ${
                        getTableRefTableName(oldColumn.type).name
                    }, but table ${getTableRefTableName(newColumn.type).name}, for screen ${screenName}`
                );
                return;
            }
        }

        if (oldColumn.type.kind === "array") {
            if (newColumn.type.kind !== "array") {
                helper.addOtherIssue(
                    `Column ${columnName} in sheet ${newTableDisplayName} is not an array, but has type ${newColumn.type.kind}, for screen ${screenName}`
                );
                return;
            }

            if (isSingleRelationType(oldColumn.type.items)) {
                // An array table reference must point to the same table.
                if (!isSingleRelationType(newColumn.type.items)) {
                    helper.addOtherIssue(
                        `Column ${columnName} in sheet ${newTableDisplayName} is not a relation array, but an array of ${newColumn.type.items.kind}, for screen ${screenName}`
                    );
                    return;
                }
                if (
                    getTableRefTableName(oldColumn.type.items).name !== getTableRefTableName(newColumn.type.items).name
                ) {
                    helper.addOtherIssue(
                        `Column ${columnName} in sheet ${newTableDisplayName} does not relate to table ${
                            getTableRefTableName(oldColumn.type.items).name
                        }, but table ${getTableRefTableName(newColumn.type.items).name}, for screen ${screenName}`
                    );
                    return;
                }
            }
        }
    }
}

class TableDescriptionHandler<TDesc extends Description> extends BaseDescriptionHandler<
    TDesc,
    TablePropertyDescriptorCase<TDesc>
> {
    constructor() {
        super(PropertyKind.Table);
    }

    public defaultUpdateForPropertyCase(
        descr: PropertyDescriptor,
        rootDesc: TDesc,
        desc: TDesc,
        pdc: TablePropertyDescriptorCase<TDesc>,
        _avoidDirectColumns: Set<TableColumn>,
        _avoidEditedColumns: Set<TableColumn>,
        _avoidIndirectColumns: Set<TableColumn>,
        tables: InputOutputTables,
        _mutatingScreenKind: MutatingScreenKind | undefined,
        schema: SchemaInspector
    ): Partial<TDesc> | undefined {
        const allowedTables = pdc.getAllowedTables(rootDesc, schema.schema, tables);
        if (allowedTables.length === 0) return undefined;
        return updatesForPropertyValue(pdc.kind, descr.property, desc, getTableName(allowedTables[0]), undefined);
    }

    public getColumnAssignmentName(_pd: PropertyDescription): string {
        return "Table";
    }
}

class EnumDescriptionHandler<TDesc extends Description, P extends BasePrimitiveValue> extends BaseDescriptionHandler<
    TDesc,
    EnumPropertyDescriptorCase<P, TDesc>
> {
    constructor() {
        super(PropertyKind.Enum);
    }

    public defaultUpdateForPropertyCase(
        descr: PropertyDescriptor,
        _rootDesc: TDesc,
        desc: TDesc,
        pdc: EnumPropertyDescriptorCase<P, TDesc>
    ): Partial<TDesc> | undefined {
        if (pdc.defaultCaseValue === undefined) {
            return updatesForProperty(descr.property, desc, undefined, undefined);
        }
        return updatesForPropertyValue(pdc.kind, descr.property, desc, pdc.defaultCaseValue, undefined);
    }

    public getEditedColumns(
        pdc: EnumPropertyDescriptorCase<P, TDesc>,
        pd: PropertyDescription,
        rootDesc: TDesc,
        tables: InputOutputTables,
        _mutatingScreenKind: MutatingScreenKind | undefined,
        ccc: ComponentConfiguratorContext
    ): EditedColumns {
        const enumValue = getEnumProperty(pd);
        if (enumValue === undefined) return [];

        for (const c of pdc.cases) {
            if (c.value !== enumValue) continue;

            if (c.columnWritten === undefined) return [];

            let table: TableGlideType | undefined = tables.output;
            if (pdc.getIndirectTable !== undefined) {
                table = pdc.getIndirectTable(tables, rootDesc, ccc);
            }
            if (table === undefined) return [];

            return [[c.columnWritten, pdc.getIndirectTable === undefined, getTableName(table)]];
        }

        return [];
    }

    public rewriteWithCase(
        descr: PropertyDescriptor,
        pdc: EnumPropertyDescriptorCase<P, TDesc>,
        pd: PropertyDescription,
        _newRootDesc: TDesc,
        newDesc: TDesc
    ): Partial<TDesc> | undefined {
        const enumValue = getEnumProperty(pd);
        if (enumValue === undefined) {
            if (pdc.defaultCaseValue === undefined) {
                logInfo("No value for enum", descr.property);
                return undefined;
            }
            return updatesForProperty(descr.property, newDesc, makeEnumProperty(pdc.defaultCaseValue), undefined);
        }
        return {};
    }

    public makeArgumentForComponent(
        { b }: ComponentBuilder,
        _table: TableGlideType,
        pd: PropertyDescription
    ): SerializedComponent | undefined {
        const v = getEnumProperty(pd);
        if (v === undefined) return undefined;

        return b.constant(v);
    }

    public getColumnAssignmentName(_pd: PropertyDescription): string {
        return "Enum";
    }
}

class StringDescriptionHandler<TDesc extends Description> extends BaseDescriptionHandler<
    TDesc,
    StringPropertyDescriptorCase
> {
    constructor() {
        super(PropertyKind.String);
    }

    public defaultUpdateForPropertyCase(
        descr: PropertyDescriptor,
        _rootDesc: TDesc,
        desc: TDesc,
        pdc: StringPropertyDescriptorCase,
        _avoidDirectColumns: Set<TableColumn>,
        _avoidEditedColumns: Set<TableColumn>,
        _avoidIndirectColumns: Set<TableColumn>,
        _tables: InputOutputTables,
        _mutatingScreenKind: MutatingScreenKind | undefined,
        _schema: SchemaInspector,
        populationMode: PopulationMode,
        getDefaultCaption: () => string | undefined
    ): Partial<TDesc> | undefined {
        if (populationMode !== PopulationMode.Rewrite || pdc.required) {
            let stringValue: string | undefined;
            if (pdc.isCaption !== undefined) {
                stringValue = getDefaultCaption() ?? pdc.isCaption;
            }

            if (stringValue === undefined && pdc.required) {
                stringValue = pdc.defaultValue ?? "";
            }
            return updatesForPropertyValue(pdc.kind, descr.property, desc, stringValue, undefined);
        } else if (pdc.defaultValue !== undefined) {
            return updatesForPropertyValue(pdc.kind, descr.property, desc, pdc.defaultValue, undefined);
        } else {
            return updatesForProperty(descr.property, desc, pdc.defaultValue, undefined);
        }
    }

    public makeValueForPropertyDescription<X, U>(
        b: ElementBuilder<X>,
        pd: PropertyDescription,
        undefinedIfEmpty: boolean,
        targetType: ColumnType | undefined
    ): X | U | undefined {
        const str = checkString(pd.value);
        // This is where we remove #emptyColumnAssignments when we build the
        // action.
        if (undefinedIfEmpty && str.trim() === "") return undefined;
        if (targetType?.kind === "boolean") {
            const maybeBoolean = asMaybeTrueOrFalse(str);
            if (maybeBoolean !== undefined) {
                return b.constant(maybeBoolean);
            }
        }
        return b.constant(str);
    }

    public makeArgumentForComponent(
        { b }: ComponentBuilder,
        _table: TableGlideType,
        pd: PropertyDescription
    ): SerializedComponent | undefined {
        const v = getStringProperty(pd);
        if (v === undefined) return undefined;

        return b.constant(v);
    }

    public getColumnAssignmentName(_pd: PropertyDescription): string {
        return "String";
    }
}

class NumberDescriptionHandler<TDesc extends Description> extends BaseDescriptionHandler<
    TDesc,
    NumberPropertyDescriptorCase
> {
    constructor() {
        super(PropertyKind.Number);
    }

    public defaultUpdateForPropertyCase(
        descr: PropertyDescriptor,
        _rootDesc: TDesc,
        desc: TDesc,
        pdc: NumberPropertyDescriptorCase,
        _avoidDirectColumns: Set<TableColumn>,
        _avoidEditedColumns: Set<TableColumn>,
        _avoidIndirectColumns: Set<TableColumn>,
        _tables: InputOutputTables,
        _mutatingScreenKind: MutatingScreenKind | undefined,
        _schema: SchemaInspector,
        populationMode: PopulationMode
    ): Partial<TDesc> | undefined {
        if (
            populationMode === PopulationMode.UserRequest ||
            pdc.required === RequiredKind.Required ||
            (populationMode === PopulationMode.Default && pdc.required === RequiredKind.NotRequiredDefaultPresent)
        ) {
            return updatesForPropertyValue(pdc.kind, descr.property, desc, pdc.defaultValue, undefined);
        } else {
            return updatesForProperty(descr.property, desc, undefined, undefined);
        }
    }

    public makeArgumentForComponent(
        { b }: ComponentBuilder,
        _table: TableGlideType,
        pd: PropertyDescription
    ): SerializedComponent | undefined {
        const v = getNumberProperty(pd);
        if (v === undefined) return undefined;

        return b.constant(v);
    }

    public getColumnAssignmentName(_pd: PropertyDescription): string {
        return "Number";
    }
}

class SwitchDescriptionHandler<TDesc extends Description> extends BaseDescriptionHandler<
    TDesc,
    SwitchPropertyDescriptorCase
> {
    constructor() {
        super(PropertyKind.Switch);
    }

    public defaultUpdateForPropertyCase(
        descr: PropertyDescriptor,
        _rootDesc: TDesc,
        desc: TDesc,
        pdc: SwitchPropertyDescriptorCase
    ): Partial<TDesc> | undefined {
        return updatesForPropertyValue(pdc.kind, descr.property, desc, pdc.defaultValue === true, undefined);
    }

    public makeArgumentForComponent(
        { b }: ComponentBuilder,
        _table: TableGlideType,
        pd: PropertyDescription
    ): SerializedComponent | undefined {
        const v = getSwitchProperty(pd);
        if (v === undefined) return undefined;

        return b.constant(v);
    }

    public getColumnAssignmentName(_pd: PropertyDescription): string {
        return "Switch";
    }
}

class ConstantDescriptionHandler<TDesc extends Description> extends BaseDescriptionHandler<
    TDesc,
    ConstantPropertyDescriptorCase
> {
    constructor() {
        super(PropertyKind.Constant);
    }

    public defaultUpdateForPropertyCase(
        descr: PropertyDescriptor,
        _rootDesc: TDesc,
        desc: TDesc,
        pdc: ConstantPropertyDescriptorCase
    ): Partial<TDesc> | undefined {
        return updatesForPropertyValue(pdc.kind, descr.property, desc, pdc.value, undefined);
    }

    public makeValueForPropertyDescription<X, U>(b: ElementBuilder<X>, pd: PropertyDescription): X | U {
        const { value } = pd;
        if (typeof value !== "string" && typeof value !== "number" && typeof value !== "boolean") {
            return panic("Constant isn't string, number, or boolean");
        }
        return b.constant(pd.value);
    }

    public makeArgumentForComponent(): SerializedComponent | undefined {
        return undefined;
    }

    public getColumnAssignmentName(_pd: PropertyDescription): string {
        return "Constant";
    }
}

class SpecialValueDescriptionHandler<TDesc extends Description> extends BaseDescriptionHandler<
    TDesc,
    SpecialValuePropertyDescriptorCase
> {
    constructor() {
        super(PropertyKind.SpecialValue);
    }

    public defaultUpdateForPropertyCase(
        descr: PropertyDescriptor,
        _rootDesc: TDesc,
        desc: TDesc,
        pdc: SpecialValuePropertyDescriptorCase
    ): Partial<TDesc> | undefined {
        return updatesForPropertyValue(pdc.kind, descr.property, desc, SpecialValueKind.Timestamp, undefined);
    }

    public makeValueForPropertyDescription<X, U>(b: ElementBuilder<X>, pd: PropertyDescription): X | U {
        const specialValueKind = checkString(pd.value) as SpecialValueKind;
        return b.clonePrimitive(b.specialValue(specialValueKind));
    }

    public getColumnAssignmentIcon(pd: PropertyDescription): string {
        const kind = pd.value as SpecialValueKind;
        const descr = findSpecialValueDescriptor(kind);
        if (descr === undefined) {
            return "separator";
        }
        return descr.img;
    }

    public getColumnAssignmentLabel(_schemaInspector: SchemaInspector, pd: PropertyDescription): string {
        assert(pd.kind === PropertyKind.SpecialValue);
        return defined(findSpecialValueDescriptor(pd.value as SpecialValueKind)).label;
    }

    public getColumnAssignmentName(_pd: PropertyDescription): string {
        return "Special value";
    }
}

class ScreenDescriptionHandler<TDesc extends Description> extends BaseDescriptionHandler<
    TDesc,
    ScreenPropertyDescriptorCase
> {
    constructor() {
        super(PropertyKind.Screen);
    }

    public defaultUpdateForPropertyCase(
        descr: PropertyDescriptor,
        _rootDesc: TDesc,
        desc: TDesc,
        pdc: ScreenPropertyDescriptorCase,
        _avoidDirectColumns: Set<TableColumn>,
        _avoidEditedColumns: Set<TableColumn>,
        _avoidIndirectColumns: Set<TableColumn>,
        _tables: InputOutputTables,
        _mutatingScreenKind: MutatingScreenKind | undefined,
        ccc: ComponentConfiguratorContext
    ): Partial<TDesc> | undefined {
        const { appDescription } = ccc;
        if (appDescription !== undefined) {
            const tab = first(getAppTabs(appDescription).filter(t => !t.hidden));
            const screenName = getScreenProperty(tab?.screenName);
            if (screenName !== undefined) {
                return updatesForPropertyValue(pdc.kind, descr.property, desc, screenName, undefined);
            }
        }
        return updatesForUntypedProperty(descr.property, undefined);
    }

    public rewriteWithCase(
        descr: PropertyDescriptor,
        _pdc: ScreenPropertyDescriptorCase,
        pd: PropertyDescription,
        _newRootDesc: TDesc,
        newDesc: TDesc,
        _tables: InputOutputTables,
        _mutatingScreenKind: MutatingScreenKind | undefined,
        _ignoreRequired: boolean,
        ccc: RewritingComponentConfiguratorContext
    ): Partial<TDesc> | undefined {
        const screen = getScreenProperty(pd);
        if (screen !== undefined) {
            const success = ccc.requireScreen(screen);
            return updatesForProperty(
                descr.property,
                newDesc,
                success ? makeScreenProperty(screen) : undefined,
                undefined
            );
        }
        return {};
    }

    public getColumnAssignmentName(_pd: PropertyDescription): string {
        return "Screen";
    }
}

class PaymentMethodDescriptionHandler<TDesc extends Description> extends BaseDescriptionHandler<
    TDesc,
    PaymentMethodPropertyDescriptorCase
> {
    constructor() {
        super(PropertyKind.PaymentMethod);
    }

    public defaultUpdateForPropertyCase(): Partial<TDesc> | undefined {
        // FIXME: If the user has a payment method set up already,
        // return an update for that payment method.
        return {};
    }

    public getColumnAssignmentName(_pd: PropertyDescription): string {
        return "Payment method";
    }
}

class FilterDescriptionHandler<TDesc extends Description> extends BaseDescriptionHandler<
    TDesc,
    FilterPropertyDescriptorCase<TDesc>
> {
    constructor() {
        super(PropertyKind.Filter);
    }

    public defaultUpdateForPropertyCase(
        descr: PropertyDescriptor,
        _rootDesc: TDesc,
        desc: TDesc,
        pdc: FilterPropertyDescriptorCase<TDesc>
    ): Partial<TDesc> | undefined {
        return updatesForPropertyValue(pdc.kind, descr.property, desc, undefined, undefined);
    }

    public rewriteWithCase(
        descr: PropertyDescriptor,
        pdc: FilterPropertyDescriptorCase<TDesc>,
        pd: PropertyDescription,
        newRootDesc: TDesc,
        newDesc: TDesc,
        tables: InputOutputTables,
        _utatingScreenKind: MutatingScreenKind | undefined,
        _ignoreRequired: boolean,
        ccc: RewritingComponentConfiguratorContext
    ): Partial<TDesc> | undefined {
        const filter = getFilterProperty(pd);
        if (filter !== undefined) {
            const table = getFilterTable(pdc, tables, newRootDesc, ccc);
            if (table === undefined) return undefined;
            const newFilter = rewriteFilter(filter, table);
            if (newFilter === undefined) return undefined;
            return updatesForProperty(descr.property, newDesc, makeFilterProperty(newFilter), undefined);
        }
        return newDesc;
    }

    public getColumnAssignmentName(_pd: PropertyDescription): string {
        return "Filter";
    }
}

class FormulaDescriptionHandler<TDesc extends Description> extends BaseDescriptionHandler<
    TDesc,
    FormulaPropertyDescriptorCase
> {
    constructor() {
        super(PropertyKind.Formula);
    }

    public defaultUpdateForPropertyCase(descr: PropertyDescriptor): Partial<TDesc> | undefined {
        return updatesForUntypedProperty(descr.property, undefined);
    }

    public getColumnAssignmentName(_pd: PropertyDescription): string {
        return "Formula";
    }
}

class TransformsDescriptionHandler<TDesc extends Description> extends BaseDescriptionHandler<
    TDesc,
    TransformsPropertyDescriptorCase<TDesc>
> {
    constructor() {
        super(PropertyKind.Transforms);
    }

    public defaultUpdateForPropertyCase(descr: PropertyDescriptor): Partial<TDesc> | undefined {
        return updatesForUntypedProperty(descr.property, []);
    }

    public getColumnAssignmentName(_pd: PropertyDescription): string {
        return "Transforms";
    }
}

class SortsDescriptionHandler<TDesc extends Description> extends BaseDescriptionHandler<
    TDesc,
    SortsPropertyDescriptorCase<TDesc>
> {
    constructor() {
        super(PropertyKind.Sorts);
    }

    public defaultUpdateForPropertyCase(descr: PropertyDescriptor): Partial<TDesc> | undefined {
        return updatesForUntypedProperty(descr.property, []);
    }

    public getColumnAssignmentName(_pd: PropertyDescription): string {
        return "Sorts";
    }
}

class ArrayDescriptionHandler<TDesc extends Description> extends BaseDescriptionHandler<
    TDesc,
    ArrayPropertyDescriptorCase
> {
    constructor() {
        super(PropertyKind.Array);
    }

    public defaultUpdateForPropertyCase(
        descr: PropertyDescriptor,
        rootDesc: TDesc,
        desc: TDesc,
        pdc: ArrayPropertyDescriptorCase,
        _avoidDirectColumns: Set<TableColumn>,
        _avoidEditedColumns: Set<TableColumn>,
        _avoidIndirectColumns: Set<TableColumn>,
        tables: InputOutputTables,
        _mutatingScreenKind: MutatingScreenKind | undefined,
        ccc: ComponentConfiguratorContext
    ): Partial<TDesc> | undefined {
        let newItem: Description | undefined;
        if (!pdc.allowEmpty) {
            newItem = populateDescription(
                () => pdc.properties,
                PopulationMode.Default,
                rootDesc,
                {} as Description,
                tables,
                ccc,
                false,
                undefined
            );
        }

        return updatesForPropertyValue(
            pdc.kind,
            descr.property,
            desc,
            definedMap(newItem, item => [item]) ?? [],
            undefined
        );
    }

    public getEditedColumns(
        pdc: ArrayPropertyDescriptorCase,
        pd: PropertyDescription,
        _rootDesc: TDesc,
        tables: InputOutputTables,
        mutatingScreenKind: MutatingScreenKind | undefined,
        ccc: ComponentConfiguratorContext,
        withActions: boolean
    ): EditedColumns {
        const items = getArrayProperty<Description>(pd);
        if (items === undefined) return [];

        const columnsEdited: EditedColumn[] = [];
        for (const item of items) {
            columnsEdited.push(
                ...getEditedColumnsForProperties(pdc.properties, item, tables, mutatingScreenKind, ccc, withActions)
            );
        }

        return columnsEdited;
    }

    public rewriteWithCase(
        descr: PropertyDescriptor,
        pdc: ArrayPropertyDescriptorCase,
        propertyDescription: PropertyDescription,
        newRootDesc: TDesc,
        newDesc: TDesc,
        tables: InputOutputTables,
        mutatingScreenKind: MutatingScreenKind | undefined,
        ignoreRequired: boolean,
        ccc: RewritingComponentConfiguratorContext
    ): Partial<TDesc> | undefined {
        const items = getArrayProperty<Description>(propertyDescription);
        if (items === undefined) return undefined;

        const newItems = mapFilterUndefined(items, item =>
            populateDescription(
                () => pdc.properties,
                PopulationMode.Rewrite,
                newRootDesc,
                item,
                tables,
                ccc,
                true,
                mutatingScreenKind,
                undefined,
                undefined,
                ignoreRequired
            )
        );
        return updatesForPropertyValue(pdc.kind, descr.property, newDesc, newItems, undefined);
    }

    public getColumnAssignmentName(_pd: PropertyDescription): string {
        return "Array";
    }
}

class ZapDescriptionHandler<TDesc extends Description> extends BaseDescriptionHandler<
    TDesc,
    ZapPropertyDescriptorCase
> {
    constructor() {
        super(PropertyKind.Zap);
    }

    public defaultUpdateForPropertyCase(
        descr: PropertyDescriptor,
        _rootDesc: TDesc,
        desc: TDesc,
        pdc: ZapPropertyDescriptorCase
    ): Partial<TDesc> | undefined {
        return updatesForPropertyValue(pdc.kind, descr.property, desc, undefined, undefined);
    }

    public makeValueForPropertyDescription<X, U>(b: ElementBuilder<X>, pd: PropertyDescription): X | U {
        return b.constant(checkString(pd.value));
    }

    public makeArgumentForComponent(
        { b }: ComponentBuilder,
        _table: TableGlideType,
        pd: PropertyDescription
    ): SerializedComponent | undefined {
        const v = getZapProperty(pd);
        if (v === undefined) return undefined;

        return b.constant(v);
    }

    public getColumnAssignmentName(_pd: PropertyDescription): string {
        return "Zap";
    }
}

class WebhookDescriptionHandler<TDesc extends Description> extends BaseDescriptionHandler<
    TDesc,
    WebhookPropertyDescriptorCase
> {
    constructor() {
        super(PropertyKind.Webhook);
    }

    public defaultUpdateForPropertyCase(
        descr: PropertyDescriptor,
        _rootDesc: TDesc,
        desc: TDesc,
        pdc: WebhookPropertyDescriptorCase
    ): Partial<TDesc> | undefined {
        return updatesForPropertyValue(pdc.kind, descr.property, desc, undefined, undefined);
    }

    public makeValueForPropertyDescription<X, U>(b: ElementBuilder<X>, pd: PropertyDescription): X | U {
        return b.constant(checkString(pd.value));
    }

    public makeArgumentForComponent(
        { b }: ComponentBuilder,
        _table: TableGlideType,
        pd: PropertyDescription
    ): SerializedComponent | undefined {
        const v = getWebhookProperty(pd);
        if (v === undefined) return undefined;

        return b.constant(v);
    }

    public getColumnAssignmentName(_pd: PropertyDescription): string {
        return "Webhook";
    }
}

class IconDescriptionHandler<TDesc extends Description> extends BaseDescriptionHandler<
    TDesc,
    IconPropertyDescriptorCase
> {
    constructor() {
        super(PropertyKind.Icon);
    }

    public defaultUpdateForPropertyCase(
        descr: PropertyDescriptor,
        _rootDesc: TDesc,
        desc: TDesc,
        pdc: IconPropertyDescriptorCase
    ): Partial<TDesc> | undefined {
        return updatesForPropertyValue(pdc.kind, descr.property, desc, undefined, undefined);
    }

    public makeValueForPropertyDescription<X, U>(b: ElementBuilder<X>, pd: PropertyDescription): X | U {
        return b.constant(checkString(pd.value));
    }

    public makeArgumentForComponent(
        { b }: ComponentBuilder,
        _table: TableGlideType,
        pd: PropertyDescription
    ): SerializedComponent | undefined {
        const v = getIconProperty(pd);
        if (v === undefined) return undefined;

        return b.constant(v);
    }

    public getColumnAssignmentName(_pd: PropertyDescription): string {
        return "Icon";
    }
}

class ActionDescriptionHandler<TDesc extends Description> extends BaseDescriptionHandler<
    TDesc,
    ActionPropertyDescriptorCase<TDesc>
> {
    constructor() {
        super(PropertyKind.Action);
    }

    public defaultUpdateForPropertyCase(
        descr: PropertyDescriptor,
        _rootDesc: TDesc,
        desc: TDesc,
        pdc: ActionPropertyDescriptorCase<TDesc>,
        _avoidDirectColumns: Set<TableColumn>,
        _avoidEditedColumns: Set<TableColumn>,
        _avoidIndirectColumns: Set<TableColumn>,
        tables: InputOutputTables,
        _mutatingScreenKind: MutatingScreenKind | undefined,
        _ccc: ComponentConfiguratorContext,
        populationMode: PopulationMode
    ): Partial<TDesc> | undefined {
        let action: ActionDescription | undefined;
        // We don't auto-populate the action if we're rewriting or the user
        // chose to unset it, unless the action is actually required.
        if (pdc.required === true || populationMode === PopulationMode.Default) {
            action = makeDefaultAction(pdc, tables, desc);
        }
        return updatesForPropertyValue(pdc.kind, descr.property, desc, action, undefined);
    }

    public getEditedColumns(
        _pdc: ActionPropertyDescriptorCase<TDesc>,
        pd: PropertyDescription,
        _rootDesc: TDesc,
        tables: InputOutputTables,
        mutatingScreenKind: MutatingScreenKind | undefined,
        ccc: ComponentConfiguratorContext,
        withActions: boolean
    ): EditedColumns {
        if (!withActions) return [];

        const v = getActionProperty(pd);
        if (v === undefined) return [];

        const handler = handlerForActionKind(v.kind);
        return handler?.getEditedColumns(v, tables, ccc, mutatingScreenKind) ?? [];
    }

    public makeValueForPropertyDescription<X, U>(b: ElementBuilder<X>, pd: PropertyDescription): X | U {
        return b.constant(checkString(pd.value));
    }

    public makeArgumentForComponent(
        { b }: ComponentBuilder,
        _table: TableGlideType,
        pd: PropertyDescription
    ): SerializedComponent | undefined {
        const v = getActionProperty(pd);
        if (v === undefined) return undefined;

        // FIXME: This can't be right, can it?
        return b.constant(v);
    }

    public getColumnAssignmentName(_pd: PropertyDescription): string {
        return "Action";
    }
}

class CompoundActionDescriptionHandler<TDesc extends Description> extends BaseDescriptionHandler<
    TDesc,
    CompoundActionPropertyDescriptorCase
> {
    constructor() {
        super(PropertyKind.CompoundAction);
    }

    public defaultUpdateForPropertyCase(
        descr: PropertyDescriptor,
        _rootDesc: TDesc,
        desc: TDesc,
        pdc: CompoundActionPropertyDescriptorCase
    ): Partial<TDesc> | undefined {
        return updatesForPropertyValue(pdc.kind, descr.property, desc, undefined, undefined);
    }

    public getEditedColumns(
        _pdc: CompoundActionPropertyDescriptorCase,
        pd: PropertyDescription,
        _rootDesc: TDesc,
        tables: InputOutputTables,
        mutatingScreenKind: MutatingScreenKind | undefined,
        ccc: ComponentConfiguratorContext
    ): EditedColumns {
        const actionID = getCompoundActionProperty(pd);
        if (actionID === undefined) return [];

        const compound = ccc.getBuilderAction(actionID);
        if (compound === undefined) return [];

        const editedColumns: EditedColumn[] = [];

        function processFlow(flow: FlowActionNode): void {
            for (const { actionDescription } of flow.actions) {
                const handler = handlerForActionKind(actionDescription.kind);
                const e = handler?.getEditedColumns(actionDescription, tables, ccc, mutatingScreenKind);
                if (e === undefined) continue;
                editedColumns.push(...e);
            }
        }

        processCompoundActionFlow(compound.action, processFlow);

        return editedColumns;
    }

    public getColumnAssignmentName(_pd: PropertyDescription): string {
        return "Compound action";
    }
}

export function registerDescriptionHandlers(): void {
    registerDescriptionHandler(new ColumnDescriptionHandler());
    registerDescriptionHandler(new TableDescriptionHandler());
    registerDescriptionHandler(new EnumDescriptionHandler());
    registerDescriptionHandler(new StringDescriptionHandler());
    registerDescriptionHandler(new ZapDescriptionHandler());
    registerDescriptionHandler(new WebhookDescriptionHandler());
    registerDescriptionHandler(new NumberDescriptionHandler());
    registerDescriptionHandler(new SwitchDescriptionHandler());
    registerDescriptionHandler(new ConstantDescriptionHandler());
    registerDescriptionHandler(new SpecialValueDescriptionHandler());
    registerDescriptionHandler(new ScreenDescriptionHandler());
    registerDescriptionHandler(new PaymentMethodDescriptionHandler());
    registerDescriptionHandler(new FilterDescriptionHandler());
    registerDescriptionHandler(new FormulaDescriptionHandler());
    registerDescriptionHandler(new TransformsDescriptionHandler());
    registerDescriptionHandler(new SortsDescriptionHandler());
    registerDescriptionHandler(new ArrayDescriptionHandler());
    registerDescriptionHandler(new IconDescriptionHandler());
    registerDescriptionHandler(new ActionDescriptionHandler());
    registerDescriptionHandler(new CompoundActionDescriptionHandler());
}
